import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TableSkeleton from "../../Utils/TableSkeleton";
import { getData, PostdataWithoutToken } from "../../API/GlobalApi";
import Notifier from "../../Utils/notify";
import "../../assets/css/StudentShortDetails.css";

const DefaulterApprovalPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);

    const student_id = queryParams.get("student_id");
    const activity_id = queryParams.get("activity_id");
    const employee_id = queryParams.get("employee_id");

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [remark, setRemark] = useState("");

    useEffect(() => {
        if (!student_id) {
            setLoading(false);
            return;
        }
        const fetchData = async () => {
            try {
                const res = await getData(
                    `students/defaulter-list/?search=${student_id}`
                );
                const result = res?.results?.[0];
                if (result) {
                    setData({
                        student_name: result.name,
                        registration_number: result.registration_number,
                        course: result.course,
                        mobile: result.mobile,
                        defaulter_remark: result.defaulter_add_remark,
                    });
                }
            } catch {
                // leave data as null — shows invalid link state
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [student_id]);

    const handleAction = async (isApproved) => {
        setActionLoading(true);
        try {
            const res = await PostdataWithoutToken(
                `academics/activities/record-exceptional-approval/`,
                {
                    student_id,
                    activity_id,
                    employee_id,
                    is_approved: isApproved,
                    remark: remark.trim(),
                }
            );
            if (res?.error || res?.detail) {
                Notifier.error(res.error || res.detail);
                return;
            }
            if (isApproved === 1) {
                navigate("/fee-approval-success", { state: { type: "defaulter_approval" } });
            } else {
                navigate("/fee-approval-cancel");
            }
        } catch {
            Notifier.error("Something went wrong.");
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="container d-flex justify-content-center align-items-center vh-100">
                <TableSkeleton />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center vh-100 text-center px-3">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D92D20" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h5 style={{ color: '#222F3E', fontWeight: 600 }}>Invalid or Expired Link</h5>
                <p style={{ color: '#888', fontSize: 14 }}>This approval link is no longer valid.</p>
            </div>
        );
    }

    return (
        <div className="container py-4 impose-responsive-font">
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: 32, marginTop: 24 }}>
                <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    background: '#FEF3F2', border: '1px solid #FDA29B',
                    borderRadius: 8, padding: '8px 18px', marginBottom: 12,
                }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span style={{ color: '#D92D20', fontWeight: 600, fontSize: 14 }}>Defaulter Approval Request</span>
                </div>
                <h2 className="templateFeeTitles" style={{ color: '#222F3E', fontWeight: 700, marginBottom: 4 }}>
                    Student Defaulter Clearance
                </h2>
                <p style={{ color: '#888', fontSize: 13 }}>
                    Review student details and approve or reject this request.
                </p>
            </div>

            {/* Student Details Card */}
            <div style={{
                background: '#fff', borderRadius: 12,
                border: '1px solid #E5E7EB', padding: '20px 24px', marginBottom: 24,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
                <h5 style={{ color: '#7F56DA', fontWeight: 600, marginBottom: 16, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Student Details
                </h5>
                <div className="row g-3 detailsContainer">
                    <div className="col-12 col-sm-6 col-md-4 contentDiv">
                        <strong>Name</strong>
                        <div>{data.student_name || '—'}</div>
                    </div>
                    <div className="col-12 col-sm-6 col-md-4 contentDiv">
                        <strong>Registration No.</strong>
                        <div style={{ color: '#7F56DA' }}>{data.registration_number || '—'}</div>
                    </div>
                    <div className="col-12 col-sm-6 col-md-4 contentDiv">
                        <strong>Course</strong>
                        <div>{data.course || '—'}</div>
                    </div>
                    <div className="col-12 col-sm-6 col-md-4 contentDiv">
                        <strong>Mobile</strong>
                        <div>{data.mobile || '—'}</div>
                    </div>
                    {data.class_name && (
                        <div className="col-12 col-sm-6 col-md-4 contentDiv">
                            <strong>Class</strong>
                            <div>{data.class_name}</div>
                        </div>
                    )}
                    {data.session && (
                        <div className="col-12 col-sm-6 col-md-4 contentDiv">
                            <strong>Session</strong>
                            <div>{data.session}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Ledger / Due Details Card */}
            {(data.amount_due !== undefined || data.amount_paid !== undefined || data.pending_amount !== undefined) && (
                <div style={{
                    background: '#fff', borderRadius: 12,
                    border: '1px solid #E5E7EB', padding: '20px 24px', marginBottom: 24,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                    <h5 style={{ color: '#7F56DA', fontWeight: 600, marginBottom: 16, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Ledger Details
                    </h5>
                    <div className="row g-3 detailsContainer">
                        <div className="col-12 col-sm-6 col-md-4 contentDiv">
                            <strong>Total Due</strong>
                            <div style={{ color: '#D92D20', fontWeight: 600 }}>₹ {data.amount_due ?? '—'}</div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 contentDiv">
                            <strong>Amount Paid</strong>
                            <div style={{ color: '#039855', fontWeight: 600 }}>₹ {data.amount_paid ?? '—'}</div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 contentDiv">
                            <strong>Pending Amount</strong>
                            <div style={{ color: '#D92D20', fontWeight: 600 }}>₹ {data.pending_amount ?? '—'}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Defaulter Remark */}
            {data.defaulter_remark && (
                <div style={{
                    background: '#FFFBEB', border: '1px solid #FCD34D',
                    borderRadius: 10, padding: '14px 18px', marginBottom: 24,
                }}>
                    <p style={{ fontWeight: 600, fontSize: 13, color: '#92400E', marginBottom: 4 }}>Defaulter Remark</p>
                    <p style={{ fontSize: 14, color: '#78350F', margin: 0 }}>{data.defaulter_remark}</p>
                </div>
            )}

            {/* Approval Remark + Buttons */}
            <div style={{
                background: '#fff', borderRadius: 12,
                border: '1px solid #E5E7EB', padding: '20px 24px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
                <label style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, display: 'block' }}>
                    Remark <span style={{ color: '#888', fontWeight: 400 }}>(optional)</span>
                </label>
                <textarea
                    rows={3}
                    value={remark}
                    onChange={e => setRemark(e.target.value)}
                    placeholder="Add a remark for this decision..."
                    style={{
                        width: '100%', border: '1px solid #ddd', borderRadius: 8,
                        padding: '10px 12px', fontSize: 14, resize: 'vertical', outline: 'none',
                        marginBottom: 20,
                    }}
                />
                <div className="d-flex flex-wrap gap-2 btnContainers justify-content-end">
                    <button
                        className="btn btn-secondary px-4"
                        onClick={() => handleAction(0)}
                        disabled={actionLoading}
                        style={{ minWidth: 120 }}
                    >
                        Reject
                    </button>
                    <button
                        className="add-btn px-4"
                        onClick={() => handleAction(1)}
                        disabled={actionLoading}
                        style={{ minWidth: 120 }}
                    >
                        {actionLoading
                            ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Processing...</>
                            : 'Approve'
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DefaulterApprovalPage;
